import type {
  CrmAccount,
  Opportunity,
  OpportunityCreate,
  OpportunityStatus,
  OpportunityUpdate,
  PipelineWithStages,
} from '@erp/shared';
import { ArrowRightLeft, FileText, Pencil, RotateCcw, Target, Trash2, Trophy, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatMoney } from '../../lib/money.js';

const STATUS_VARIANT: Record<
  OpportunityStatus,
  'info' | 'success' | 'warning' | 'danger' | 'secondary'
> = {
  open: 'info',
  won: 'success',
  lost: 'secondary',
};

export function OpportunitiesPage(): JSX.Element {
  const { t, i18n } = useTranslation('crm');
  const navigate = useNavigate();
  const { t: tc } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const canWrite = usePermission('crm.opportunities.write');
  const [items, setItems] = useState<Opportunity[] | null>(null);
  const [accounts, setAccounts] = useState<CrmAccount[]>([]);
  const [pipelines, setPipelines] = useState<PipelineWithStages[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [moveStaging, setMoveStaging] = useState<Opportunity | null>(null);
  const [statusFilter, setStatusFilter] = useState<OpportunityStatus | ''>('open');

  // One-shot fetch on mount for the two dropdown catalogs:
  //   * active CRM accounts (Create / Edit form account picker)
  //   * pipelines with stages (Move-stage dialog)
  // Both are small (single-tenant catalogs) so an unbounded fetch is fine.
  useEffect(() => {
    void (async () => {
      try {
        const [accRes, pipRes] = await Promise.all([
          api().get<{ items: CrmAccount[] }>('/crm/accounts?status=active'),
          api().get<{ items: PipelineWithStages[] }>('/crm/pipelines'),
        ]);
        setAccounts(accRes.data.items);
        setPipelines(pipRes.data.items);
      } catch {
        // Non-fatal: the create form falls back to a notice when no
        // accounts are loaded; move-stage button is hidden when there
        // are no pipelines.
      }
    })();
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    const qs = params.toString();
    const url = qs.length > 0 ? `/crm/opportunities?${qs}` : '/crm/opportunities';
    const r = await api().get<{ items: Opportunity[] }>(url);
    setItems(r.data.items);
  }, [statusFilter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Stage-id → name lookup, built once from the pipelines catalog. Lives
  // above the early-return so the hook order is stable across renders.
  // Opportunity rows expose stageId (UUID) only; the table needs this
  // to render a readable "Stage" column. Falls back to "—" for any
  // orphaned stageId (e.g. its pipeline got deactivated).
  const stageNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pipelines) {
      for (const s of p.stages) m.set(s.id, s.name);
    }
    return m;
  }, [pipelines]);

  // Same idea for accounts so the "Account" column resolves the
  // company name instead of leaving the operator to open Edit just to
  // see which deal is which. Only active accounts come back from
  // /crm/accounts?status=active, so a deactivated account's deals
  // will fall through to the "—" path.
  const accountById = useMemo(() => {
    const m = new Map<string, CrmAccount>();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onWin = async (o: Opportunity): Promise<void> => {
    if (!(await confirmDialog({ message: t('opportunities.actions.confirmWin', { name: o.name }) }))) return;
    // window.prompt for the optional reason — matches the existing confirm-
    // dialog UX in this codebase (used by deactivate/reactivate flows). null
    // means the operator cancelled the prompt → abort. Empty string means
    // they hit OK without typing → send no reason. Any other → send it.
    const reason = window.prompt(t('opportunities.actions.winReason'));
    if (reason === null) return;
    try {
      await api().post(`/crm/opportunities/${o.id}/win`, reason.trim() ? { reason: reason.trim() } : {});
      await reload();
    } catch {
      toast.error(t('opportunities.errors.actionFailed'));
    }
  };

  /**
   * Raise a quotation from the deal.
   *
   * Seeds the customer, currency and value onto a real quotation and links the
   * two, then hands the operator straight to the document to put the actual
   * goods on it — the opportunity has a value, not a basket, so there are no
   * lines to copy.
   */
  const onQuote = async (o: Opportunity): Promise<void> => {
    if (!(await confirmDialog({ message: t('opportunities.actions.confirmQuote', { name: o.name }) }))) return;
    try {
      const r = await api().post<{ quotationId: string; quotationNumber: string }>(
        `/crm/opportunities/${o.id}/quote`, {},
      );
      toast.success(t('opportunities.actions.quoted', { number: r.data.quotationNumber }));
      navigate(`/sales/quotations/${r.data.quotationId}`);
    } catch (err) {
      // Two refusals are worth naming: the account is not linked to a customer
      // (nothing to address the quotation to), and the deal already has one.
      const d = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = d?.cause?.code ?? d?.code;
      if (code === 'account_not_linked_to_customer') toast.error(t('opportunities.errors.accountNotLinked'));
      else if (code === 'opportunity_already_quoted') toast.error(t('opportunities.errors.alreadyQuoted'));
      else toast.error(t('opportunities.errors.actionFailed'));
    }
  };

  const onLose = async (o: Opportunity): Promise<void> => {
    if (!(await confirmDialog({ message: t('opportunities.actions.confirmLose', { name: o.name }), tone: 'danger' }))) return;
    const reason = window.prompt(t('opportunities.actions.loseReason'));
    if (reason === null) return;
    try {
      await api().post(`/crm/opportunities/${o.id}/lose`, reason.trim() ? { reason: reason.trim() } : {});
      await reload();
    } catch {
      toast.error(t('opportunities.errors.actionFailed'));
    }
  };

  const onReopen = async (o: Opportunity): Promise<void> => {
    if (!(await confirmDialog({ message: t('opportunities.actions.confirmReopen', { name: o.name }) }))) return;
    try {
      await api().post(`/crm/opportunities/${o.id}/reopen`, {});
      await reload();
    } catch {
      toast.error(t('opportunities.errors.actionFailed'));
    }
  };

  const onDelete = async (o: Opportunity): Promise<void> => {
    if (!(await confirmDialog({ message: t('opportunities.actions.confirmDelete', { name: o.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/crm/opportunities/${o.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const openEdit = (o: Opportunity): void => {
    setShowCreate(false);
    setEditing(o);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: DataTableColumn<Opportunity>[] = [
    {
      id: 'code',
      header: t('opportunities.columns.code'),
      sortValue: (o) => o.code,
      searchValue: (o) => o.code,
      cellClassName: 'font-mono text-xs',
      cell: (o) => o.code,
    },
    {
      id: 'name',
      header: t('opportunities.columns.name'),
      sortValue: (o) => o.name,
      searchValue: (o) => o.name,
      cellClassName: 'font-medium',
      cell: (o) => o.name,
    },
    {
      id: 'account',
      header: t('opportunities.columns.account'),
      sortValue: (o) => accountById.get(o.accountId)?.name ?? '',
      searchValue: (o) => {
        const acc = accountById.get(o.accountId);
        return acc ? `${acc.code} ${acc.name}` : '';
      },
      cellClassName: 'text-xs',
      cell: (o) => {
        const acc = accountById.get(o.accountId);
        return acc ? (
          <>
            <span className="font-mono text-fg-muted">{acc.code}</span>{' '}
            <span className="font-medium">{acc.name}</span>
          </>
        ) : (
          <span className="text-fg-muted">—</span>
        );
      },
    },
    {
      id: 'amount',
      header: t('opportunities.columns.amount'),
      align: 'end',
      sortValue: (o) => Number(o.amount),
      cellClassName: 'font-mono tabular-nums',
      cell: (o) =>
        o.amount != null && o.currencyCode
          ? formatMoney(o.amount, { locale: i18n.language, currency: o.currencyCode })
          : <span className="text-fg-muted">—</span>,
    },
    {
      id: 'probability',
      header: t('opportunities.columns.probability'),
      align: 'end',
      sortValue: (o) => Number(o.probability),
      cellClassName: 'font-mono tabular-nums',
      cell: (o) => <>{o.probability}%</>,
    },
    {
      id: 'closeDate',
      header: t('opportunities.columns.closeDate'),
      sortValue: (o) => o.expectedCloseDate ?? '',
      cellClassName: 'font-mono text-xs',
      cell: (o) => o.expectedCloseDate ?? '—',
    },
    {
      id: 'stage',
      header: t('opportunities.columns.stage'),
      sortValue: (o) => stageNameById.get(o.stageId) ?? '',
      searchValue: (o) => stageNameById.get(o.stageId) ?? '',
      cellClassName: 'text-fg-muted',
      cell: (o) => stageNameById.get(o.stageId) ?? '—',
    },
    {
      id: 'status',
      header: t('opportunities.columns.status'),
      sortValue: (o) => t(`opportunities.status.${o.status}`),
      searchValue: (o) => t(`opportunities.status.${o.status}`),
      cell: (o) => (
        <Badge variant={STATUS_VARIANT[o.status]}>{t(`opportunities.status.${o.status}`)}</Badge>
      ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-56',
            cell: (o: Opportunity) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(o)}
                  aria-label={`${t('opportunities.actions.edit')} ${o.name}`}
                  title={t('opportunities.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {o.status === 'open' && pipelines.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMoveStaging(o)}
                    aria-label={`${t('opportunities.actions.moveStage')} ${o.name}`}
                    title={t('opportunities.actions.moveStage')}
                  >
                    <ArrowRightLeft className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                {o.status === 'open' ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onQuote(o)}
                      aria-label={`${t('opportunities.actions.quote')} ${o.name}`}
                      title={t('opportunities.actions.quote')}
                      disabled={Boolean(o.quotationId)}
                    >
                      <FileText className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onWin(o)}
                      aria-label={`${t('opportunities.actions.win')} ${o.name}`}
                      title={t('opportunities.actions.win')}
                    >
                      <Trophy className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onLose(o)}
                      aria-label={`${t('opportunities.actions.lose')} ${o.name}`}
                      title={t('opportunities.actions.lose')}
                    >
                      <XCircle className="h-4 w-4" aria-hidden />
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReopen(o)}
                    aria-label={`${t('opportunities.actions.reopen')} ${o.name}`}
                    title={t('opportunities.actions.reopen')}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(o)}
                  aria-label={`${t('opportunities.actions.delete')} ${o.name}`}
                  title={t('opportunities.actions.delete')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('opportunities.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('opportunities.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && !moveStaging && (
          <Button onClick={() => setShowCreate(true)}>{t('opportunities.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreateOpportunityForm
          accounts={accounts}
          baseCurrency={tenant?.baseCurrencyCode ?? 'USD'}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditOpportunityForm
          opportunity={editing}
          accounts={accounts}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      {moveStaging && (
        <MoveStageForm
          opportunity={moveStaging}
          pipelines={pipelines}
          onCancel={() => setMoveStaging(null)}
          onSaved={async () => {
            setMoveStaging(null);
            await reload();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          value={statusFilter}
          onChange={(e) => setStatusFilter((e.target.value as OpportunityStatus | ''))}
          aria-label={t('opportunities.statusFilter')}
        >
          <option value="">{t('opportunities.allStatuses')}</option>
          <option value="open">{t('opportunities.status.open')}</option>
          <option value="won">{t('opportunities.status.won')}</option>
          <option value="lost">{t('opportunities.status.lost')}</option>
        </select>
      </div>

      {items.length === 0 && !showCreate && !editing && !moveStaging ? (
        <EmptyState
          icon={<Target className="h-8 w-8" aria-hidden />}
          title={t('opportunities.empty')}
          description={t('opportunities.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('opportunities.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : items.length === 0 ? null : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={items}
            rowKey={(o) => o.id}
            searchPlaceholder={t('opportunities.searchPlaceholder')}
          />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface CreateOpportunityFormProps {
  accounts: CrmAccount[];
  baseCurrency: string;
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}

/**
 * Minimal Create form for opportunities. Covers the required field
 * (account) + the four most-typed bits (name, amount, currency,
 * expected-close). Skips:
 *   * code — server auto-generates when absent
 *   * pipelineId / stageId — server picks the tenant's default
 *     pipeline + its first stage; a pipeline/stage selector is a
 *     real but bigger UI piece (deferred)
 *   * primaryContactId, ownerUserId, probability, notes — power-user
 *     fields; an edit form (also deferred) is where they belong
 *
 * The amount field is optional in the schema; we send `undefined` when
 * blank so the server stores NULL instead of '0'.
 */
function CreateOpportunityForm({
  accounts,
  baseCurrency,
  onCancel,
  onCreated,
}: CreateOpportunityFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const [form, setForm] = useState<OpportunityCreate>({
    name: '',
    accountId: accounts[0]?.id ?? '',
    currencyCode: baseCurrency,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!form.accountId) {
      setError(t('opportunities.errors.noAccounts'));
      return;
    }
    setSaving(true);
    try {
      const payload: OpportunityCreate = {
        name: form.name.trim(),
        accountId: form.accountId,
        currencyCode: form.currencyCode,
        // Strip empty amount / date so the server treats them as
        // "unset" instead of failing the regex / date validator.
        amount: form.amount && form.amount.trim() ? form.amount.trim() : undefined,
        expectedCloseDate:
          form.expectedCloseDate && form.expectedCloseDate.length > 0
            ? form.expectedCloseDate
            : undefined,
      };
      await api().post('/crm/opportunities', payload);
      await onCreated();
    } catch {
      setError(t('opportunities.errors.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  // No active accounts → can't satisfy the required accountId field.
  // Render an inline notice instead of an unusable form.
  if (accounts.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="mb-2 text-lg font-semibold">{t('opportunities.create.title')}</h2>
        <p className="text-sm text-danger" role="alert">
          {t('opportunities.errors.noAccounts')}
        </p>
        <div className="mt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('opportunities.create.cancel')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('opportunities.create.title')}</h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="o-name" label={t('opportunities.create.name')} required>
          <Input
            id="o-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="o-account" label={t('opportunities.create.account')} required>
          <select
            id="o-account"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.accountId}
            onChange={(e) => setForm({ ...form, accountId: e.target.value })}
            required
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="o-amount" label={t('opportunities.create.amount')}>
          <Input
            id="o-amount"
            value={form.amount ?? ''}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            inputMode="decimal"
            placeholder="0.00"
          />
        </FormField>
        <FormField id="o-currency" label={t('opportunities.create.currency')}>
          <CurrencySelect
            id="o-currency"
            value={form.currencyCode ?? ''}
            onChange={(c) => setForm({ ...form, currencyCode: c })}
            includeEmpty
          />
        </FormField>
        <FormField id="o-close" label={t('opportunities.create.expectedClose')}>
          <Input
            id="o-close"
            type="date"
            value={form.expectedCloseDate ?? ''}
            onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })}
          />
        </FormField>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {t('opportunities.create.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('opportunities.create.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditOpportunityFormProps {
  opportunity: Opportunity;
  accounts: CrmAccount[];
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

/**
 * Edit form for opportunities. Mirrors CreateOpportunityForm's fields
 * and adds the two power-user knobs:
 *   * probability — 0..100 integer, useful for "raise the deal
 *     from 50% to 80% on a hot lead" workflow
 *   * notes — free-text, what was said on the call
 *
 * Same intentional omissions as create: code (auto-generated, FK
 * target), pipelineId / stageId (separate Move-stage flow — needs
 * cascading dropdowns and isn't shipped here), primaryContactId,
 * ownerUserId.
 *
 * Account is editable here because OpportunityUpdateSchema accepts
 * accountId. Re-assigning a deal to a different account is a real
 * workflow (M&A, parent/child swap) — the constraint is just that
 * the target account stays active.
 */
function EditOpportunityForm({
  opportunity,
  accounts,
  onCancel,
  onSaved,
}: EditOpportunityFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const [form, setForm] = useState<OpportunityUpdate>({
    name: opportunity.name,
    accountId: opportunity.accountId,
    amount: opportunity.amount,
    currencyCode: opportunity.currencyCode,
    expectedCloseDate: opportunity.expectedCloseDate ?? undefined,
    probability: opportunity.probability,
    notes: opportunity.notes ?? undefined,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: OpportunityUpdate = {
        name: form.name?.trim() || undefined,
        accountId: form.accountId,
        // amount: empty → server NULLs; whitespace-trimmed otherwise.
        amount: form.amount && form.amount.trim() ? form.amount.trim() : undefined,
        currencyCode: form.currencyCode,
        expectedCloseDate:
          form.expectedCloseDate && form.expectedCloseDate.length > 0
            ? form.expectedCloseDate
            : null,
        probability:
          form.probability && form.probability.toString().trim()
            ? form.probability
            : undefined,
        notes: form.notes && form.notes.trim() ? form.notes.trim() : null,
      };
      await api().patch(`/crm/opportunities/${opportunity.id}`, patch);
      await onSaved();
    } catch {
      setError(t('opportunities.errors.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Ensure the current opportunity's account is in the dropdown even
  // if it's been deactivated since the deal was created — otherwise the
  // user can't see what was selected when they opened the form.
  const currentAcc = accounts.find((a) => a.id === opportunity.accountId);
  const accountOptions =
    currentAcc || !opportunity.accountId
      ? accounts
      : [{ id: opportunity.accountId, name: `${opportunity.accountId.slice(0, 8)}…` } as CrmAccount, ...accounts];

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('opportunities.edit.title', {
          code: opportunity.code,
          name: opportunity.name,
        })}
      </h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="oe-code" label="Code" required>
          <Input
            id="oe-code"
            value={opportunity.code}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="oe-name" label={t('opportunities.create.name')} required>
          <Input
            id="oe-name"
            value={form.name ?? ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="oe-account" label={t('opportunities.create.account')} required>
          <select
            id="oe-account"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.accountId ?? ''}
            onChange={(e) => setForm({ ...form, accountId: e.target.value })}
            required
          >
            {accountOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="oe-amount" label={t('opportunities.create.amount')}>
          <Input
            id="oe-amount"
            value={form.amount ?? ''}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            inputMode="decimal"
            placeholder="0.00"
          />
        </FormField>
        <FormField id="oe-currency" label={t('opportunities.create.currency')}>
          <CurrencySelect
            id="oe-currency"
            value={form.currencyCode ?? ''}
            onChange={(c) => setForm({ ...form, currencyCode: c })}
            includeEmpty
          />
        </FormField>
        <FormField id="oe-close" label={t('opportunities.create.expectedClose')}>
          <Input
            id="oe-close"
            type="date"
            value={form.expectedCloseDate ?? ''}
            onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })}
          />
        </FormField>
        <FormField id="oe-prob" label={t('opportunities.edit.probability')}>
          <Input
            id="oe-prob"
            value={form.probability ?? ''}
            onChange={(e) => setForm({ ...form, probability: e.target.value })}
            inputMode="numeric"
            placeholder="0–100"
          />
        </FormField>
        <div className="md:col-span-2">
          <FormField id="oe-notes" label={t('opportunities.edit.notes')}>
            <textarea
              id="oe-notes"
              className="w-full min-h-[80px] rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </FormField>
        </div>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {t('opportunities.edit.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('opportunities.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface MoveStageFormProps {
  opportunity: Opportunity;
  pipelines: PipelineWithStages[];
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

/**
 * Cascading pipeline → stage picker for the Move-stage action.
 *
 * Renders inline (matches the existing Create/Edit form pattern on
 * this page) instead of as a Radix Dialog — keeps the UX consistent
 * for operators who already know "click action → form appears above
 * the table".
 *
 * Edge cases handled:
 *   * The opportunity's current pipeline may have been deactivated /
 *     deleted since the deal opened — we still let the user pick from
 *     `pipelines` (the active set returned by GET /crm/pipelines) and
 *     move the deal off the orphaned pipeline. The form pre-selects
 *     the first active pipeline in that case.
 *   * Picking the same stage as the current one is a no-op on the
 *     server. We disable the Submit button in that case so the
 *     operator doesn't get confused by a "save" that doesn't change
 *     anything.
 *   * Won/lost stages are intentionally still selectable from this
 *     dropdown — the Win/Lose buttons are the encouraged path (they
 *     accept a `reason` note), but operators are free to flip stage
 *     directly if they want to bypass the dialog.
 */
function MoveStageForm({ opportunity, pipelines, onCancel, onSaved }: MoveStageFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const initialPipelineId =
    pipelines.find((p) => p.id === opportunity.pipelineId)?.id ?? pipelines[0]?.id ?? '';
  const [pipelineId, setPipelineId] = useState(initialPipelineId);
  // Default to the current stage if it still belongs to the chosen
  // pipeline; otherwise the first stage of that pipeline.
  const initialPipeline = pipelines.find((p) => p.id === initialPipelineId);
  const initialStageId =
    initialPipeline?.stages.find((s) => s.id === opportunity.stageId)?.id ??
    initialPipeline?.stages[0]?.id ?? '';
  const [stageId, setStageId] = useState(initialStageId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pipeline = pipelines.find((p) => p.id === pipelineId);
  const stages = pipeline?.stages ?? [];
  const currentStageName = pipelines
    .flatMap((p) => p.stages)
    .find((s) => s.id === opportunity.stageId)?.name;

  // Picking the same stage the opportunity already sits in is a
  // server no-op; disable the submit so the operator sees the lack
  // of change is intentional.
  const isNoop = stageId === opportunity.stageId;

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!stageId || isNoop) return;
    setSaving(true);
    setError(null);
    try {
      await api().post(`/crm/opportunities/${opportunity.id}/move-stage`, { stageId });
      await onSaved();
    } catch {
      setError(t('opportunities.errors.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  // When the operator changes pipeline, reset the stage to the first
  // stage of the new pipeline (the old stageId belongs to the prior
  // pipeline so it'd 404 on save).
  const onPipelineChange = (id: string): void => {
    setPipelineId(id);
    const p = pipelines.find((pp) => pp.id === id);
    setStageId(p?.stages[0]?.id ?? '');
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('opportunities.moveStage.title', { name: opportunity.name })}
      </h2>
      {currentStageName && (
        <p className="mb-3 text-sm text-fg-muted">
          {t('opportunities.moveStage.currentStage')}:{' '}
          <span className="font-medium">{currentStageName}</span>
        </p>
      )}
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="ms-pipeline" label={t('opportunities.moveStage.pipeline')} required>
          <select
            id="ms-pipeline"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={pipelineId}
            onChange={(e) => onPipelineChange(e.target.value)}
            required
          >
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="ms-stage" label={t('opportunities.moveStage.stage')} required>
          <select
            id="ms-stage"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            required
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormField>
        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving} disabled={isNoop}>
            {t('opportunities.moveStage.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('opportunities.moveStage.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
